import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getEvidenceReviewTrafficLight, createGeneralReviewMessage, closeGeneralReviewMessage } from '../../API/apiService';
import Loader from '../../components/loader';
import {downloadTrafficLightExcel, TRAFFIC_LIGHT_STATES} from './trafficLightExport';
import './style.scss';

const DAY_NAMES = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];

function toDateKey(date) {
  const parsedDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(parsedDate);
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function mondayOf(date) {
  const result = new Date(date);
  result.setHours(12, 0, 0, 0);
  const day = result.getDay();
  result.setDate(result.getDate() - (day === 0 ? 6 : day - 1));
  return result;
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function formatShortDate(value) {
  if (!value) return '—';
  const parsedDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return '—';
  return new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota', day: '2-digit', month: 'short', year: 'numeric'
  }).format(parsedDate);
}

function normalizeRecord(record) {
  return {
    ...record,
    meetingGroupId: record?.meetingGroupId ?? record?.meeting_group_id,
    incidenceActionId: record?.incidenceActionId ?? record?.incidence_action_id,
    mediatorId: record?.mediatorId ?? record?.mediator_id,
    mediatorName: record?.mediatorName ?? record?.mediator_name ?? 'Mediador',
    reviewerId: record?.reviewerId ?? record?.reviewer_id,
    requestedAt: record?.requestedAt ?? record?.requested_at,
    dueAt: record?.dueAt ?? record?.due_at,
    respondedAt: record?.respondedAt ?? record?.responded_at,
    closedAt: record?.closedAt ?? record?.closed_at
  };
}

function getMediatorEvidencePath(record) {
  if (record?.evidenceType === 'incidencia' && record?.incidenceActionId) {
    return `/acciones-incidencia/${record.incidenceActionId}`;
  }
  if (record?.evidenceType === 'concertacion' && record?.schoolId) {
    return `/exploracion/${record.schoolId}`;
  }
  if (record?.evidenceType === 'socializacion' && record?.schoolId) {
    const number = Number(record.meetingOrder) === 2 ? 2 : 1;
    return `/socializacion/${record.schoolId}?number=${number}`;
  }
  return `/escuelas/grupo/lista_de_encuentros/encuentro?encuentro=${record?.meetingGroupId}`;
}

function ReviewerTrafficLight() {
  const location = useLocation();
  const isReviewerView = location.pathname.startsWith('/revisor');
  const isAdminView = location.pathname.startsWith('/administrador');
  const isOversightView = isReviewerView || isAdminView;
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [activeFilter, setActiveFilter] = useState('');
  const [showMediatorFilter, setShowMediatorFilter] = useState(false);
  const [selectedMediatorIds, setSelectedMediatorIds] = useState([]);
  const [mediatorType, setMediatorType] = useState('');
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageMediatorId, setMessageMediatorId] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showDownloadForm, setShowDownloadForm] = useState(false);
  const [downloadStartDate, setDownloadStartDate] = useState(() => toDateKey(mondayOf(new Date())));
  const [downloadEndDate, setDownloadEndDate] = useState(() => toDateKey(addDays(mondayOf(new Date()), 6)));
  const [downloadStates, setDownloadStates] = useState(() => TRAFFIC_LIGHT_STATES.map(state => state.value));
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const detailRef = useRef(null);
  const mediatorFilterRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setSelectedRecord(null);
    setActiveFilter('');
    getEvidenceReviewTrafficLight(toDateKey(weekStart)).then(result => {
      if (!mounted) return;
      const normalizedResult = result || {mediators: [], records: [], summary: {}};
      setData({
        ...normalizedResult,
        records: (normalizedResult.records || []).map(normalizeRecord)
      });
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [weekStart, refreshKey]);

  useEffect(() => {
    if (!selectedRecord) return undefined;

    function closeWhenClickingOutside(event) {
      if (detailRef.current && !detailRef.current.contains(event.target)) {
        setSelectedRecord(null);
      }
    }

    document.addEventListener('pointerdown', closeWhenClickingOutside);
    return () => document.removeEventListener('pointerdown', closeWhenClickingOutside);
  }, [selectedRecord]);

  useEffect(() => {
    if (!showMediatorFilter) return undefined;
    function closeMediatorFilter(event) {
      if (mediatorFilterRef.current && !mediatorFilterRef.current.contains(event.target)) {
        setShowMediatorFilter(false);
      }
    }
    document.addEventListener('pointerdown', closeMediatorFilter);
    return () => document.removeEventListener('pointerdown', closeMediatorFilter);
  }, [showMediatorFilter]);

  const weekdays = useMemo(
    () => {
      const hasWeekendRecords = (data?.records || []).some(record =>
        [5, 6].some(offset => toDateKey(record.requestedAt) === toDateKey(addDays(weekStart, offset)))
      );
      return DAY_NAMES.slice(0, hasWeekendRecords ? 7 : 5).map((name, index) => ({name, date: addDays(weekStart, index)}));
    },
    [weekStart, data]
  );
  const mediatorTypes = useMemo(
    () => Array.from(new Set((data?.mediators || []).map(mediator => mediator.roleName).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'es')),
    [data]
  );
  const mediatorFilteredList = useMemo(
    () => (data?.mediators || []).filter(mediator =>
      (!mediatorType || mediator.roleName === mediatorType) &&
      (!selectedMediatorIds.length || selectedMediatorIds.includes(Number(mediator.id)))
    ),
    [data, mediatorType, selectedMediatorIds]
  );
  const mediatorFilteredRecords = useMemo(() => {
    const mediatorIds = new Set(mediatorFilteredList.map(mediator => Number(mediator.id)));
    return (data?.records || []).filter(record => mediatorIds.has(Number(record.mediatorId)));
  }, [data, mediatorFilteredList]);
  const filteredRecords = useMemo(
    () => mediatorFilteredRecords.filter(record =>
      !activeFilter || record.trackingState === activeFilter
    ),
    [mediatorFilteredRecords, activeFilter]
  );
  const recordsByCell = useMemo(() => {
    const grouped = new Map();
    filteredRecords.forEach(record => {
      const requestedDateKey = toDateKey(record.requestedAt);
      if (!record.mediatorId || !requestedDateKey) return;
      const key = `${record.mediatorId}-${requestedDateKey}`;
      grouped.set(key, [...(grouped.get(key) || []), record]);
    });
    return grouped;
  }, [filteredRecords]);
  const visibleMediators = useMemo(() => {
    if (!activeFilter) return mediatorFilteredList;
    const mediatorIds = new Set(filteredRecords.map(record => Number(record.mediatorId)));
    return mediatorFilteredList.filter(mediator => mediatorIds.has(Number(mediator.id)));
  }, [mediatorFilteredList, activeFilter, filteredRecords]);
  const summary = useMemo(() => mediatorFilteredRecords.reduce((totals, record) => {
    if (record.trackingState === 'open') totals.open += 1;
    if (record.trackingState === 'due_soon') totals.dueSoon += 1;
    if (record.trackingState === 'overdue') totals.overdue += 1;
    if (record.trackingState === 'responded') totals.responded += 1;
    return totals;
  }, {open: 0, dueSoon: 0, overdue: 0, responded: 0}), [mediatorFilteredRecords]);

  function toggleFilter(filter) {
    setSelectedRecord(null);
    setActiveFilter(current => current === filter ? '' : filter);
  }

  function toggleMediator(mediatorId) {
    const normalizedId = Number(mediatorId);
    setSelectedMediatorIds(current => current.includes(normalizedId)
      ? current.filter(id => id !== normalizedId)
      : [...current, normalizedId]
    );
  }

  function openDownloadForm() {
    setDownloadStartDate(toDateKey(weekStart));
    setDownloadEndDate(toDateKey(addDays(weekStart, 6)));
    setDownloadStates(TRAFFIC_LIGHT_STATES.map(state => state.value));
    setDownloadError('');
    setShowDownloadForm(true);
  }

  function toggleDownloadState(state) {
    setDownloadStates(current => current.includes(state)
      ? current.filter(item => item !== state)
      : [...current, state]
    );
  }

  async function handleDownload(event) {
    event.preventDefault();
    if (downloading) return;
    if (!downloadStartDate || !downloadEndDate) {
      setDownloadError('Selecciona las fechas de inicio y fin del período.');
      return;
    }
    if (downloadEndDate < downloadStartDate) {
      setDownloadError('La fecha final debe ser igual o posterior a la fecha inicial.');
      return;
    }
    if (!downloadStates.length) {
      setDownloadError('Selecciona al menos un estado.');
      return;
    }

    setDownloading(true);
    setDownloadError('');
    const result = await getEvidenceReviewTrafficLight(downloadStartDate, downloadEndDate);
    setDownloading(false);
    if (!result) {
      setDownloadError('No fue posible obtener la información del período seleccionado.');
      return;
    }

    const records = (result.records || [])
      .map(normalizeRecord)
      .filter(record => downloadStates.includes(record.trackingState));
    if (!records.length) {
      setDownloadError('No hay registros para el período y los estados seleccionados.');
      return;
    }

    downloadTrafficLightExcel({
      records,
      mediators: result.mediators || [],
      startDate: downloadStartDate,
      endDate: downloadEndDate
    });
    setShowDownloadForm(false);
    setNotice(`${records.length} ${records.length === 1 ? 'registro descargado' : 'registros descargados'}.`);
  }

  const hasMediatorFilters = Boolean(mediatorType || selectedMediatorIds.length);

  async function sendMessage(event) {
    event.preventDefault();
    if (saving || !message.trim() || !messageMediatorId) return;
    setSaving(true);
    const result = await createGeneralReviewMessage({mediatorId: Number(messageMediatorId), observations: message.trim()});
    setSaving(false);
    if (!result) return;
    setMessage('');
    setMessageMediatorId('');
    setShowMessageForm(false);
    setSelectedMediatorIds([]);
    setMediatorType('');
    setWeekStart(mondayOf(new Date()));
    setNotice('Mensaje enviado. El mediador puede consultarlo en su semáforo.');
  }

  async function closeMessage() {
    if (saving) return;
    setSaving(true);
    const result = await closeGeneralReviewMessage(selectedRecord.id);
    setSaving(false);
    if (!result) return;
    setSelectedRecord(null);
    setRefreshKey(current => current + 1);
    setNotice('Solicitud cerrada.');
  }

  return (
    <section className='reviewer-traffic'>
      <header className='reviewer-traffic__topbar'>
        <Link to={isReviewerView ? '/revisor' : isAdminView ? '/administrador' : '/'}>
          <span className='material-symbols-rounded'>arrow_back</span>
          {isReviewerView ? 'Centro de revisión' : isAdminView ? 'Panel administrador' : 'Página principal'}
        </Link>
        <div><span className='material-symbols-rounded'>traffic</span> Semáforo</div>
      </header>

      <main className='reviewer-traffic__content'>
        <div className='reviewer-traffic__heading'>
          <div>
            <span>SEGUIMIENTO DE SUBSANACIONES</span>
            <h1>{isOversightView ? 'Semáforo semanal' : 'Mi semáforo semanal'}</h1>
            <p>{isOversightView ? 'Controla los cuatro días hábiles disponibles para corregir evidencias.' : 'Consulta las solicitudes de subsanación y el tiempo disponible para responder.'}</p>
          </div>
          <div className='reviewer-traffic__week-navigation'>
            <button type='button' className='reviewer-traffic__download-button' onClick={openDownloadForm}>
              <span className='material-symbols-rounded'>download</span>
              Descargar
            </button>
            <p className='reviewer-traffic__previous-overdue' role='status'>
              <span className='material-symbols-rounded'>error</span>
              {Number(data?.summary?.previousWeeksOverdue || 0) === 1
                ? '1 solicitud vencida de semanas anteriores'
                : `${Number(data?.summary?.previousWeeksOverdue || 0)} solicitudes vencidas de semanas anteriores`}
            </p>
            <div className='reviewer-traffic__week-controls'>
              <button type='button' onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label='Semana anterior'>
                <span className='material-symbols-rounded'>chevron_left</span>
              </button>
              <strong>{formatShortDate(weekStart)} — {formatShortDate(addDays(weekStart, 4))}</strong>
              <button type='button' onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label='Semana siguiente'>
                <span className='material-symbols-rounded'>chevron_right</span>
              </button>
              <button type='button' className='is-today' onClick={() => setWeekStart(mondayOf(new Date()))}>Hoy</button>
            </div>
          </div>
        </div>

        {isReviewerView && (
          <div className='reviewer-traffic__messages'>
            <button type='button' onClick={() => setShowMessageForm(current => !current)} aria-expanded={showMessageForm} disabled={saving}>
              {showMessageForm ? 'Cancelar' : 'Escribir mensaje general'}
            </button>
            {showMessageForm && <form onSubmit={sendMessage}>
              <h2>Mensaje sin evidencia asociada</h2>
              <p>Indica qué evidencias faltan por cargar. El mediador tendrá cuatro días hábiles para atender la solicitud.</p>
              <label>Mediador
                <select required value={messageMediatorId} onChange={event => setMessageMediatorId(event.target.value)} disabled={saving || loading}>
                  <option value=''>Selecciona un mediador</option>
                  {(data?.mediators || []).map(mediator => <option key={mediator.id} value={mediator.id}>{mediator.fullName}</option>)}
                </select>
              </label>
              <label>Mensaje
                <textarea required maxLength={2000} rows={4} value={message} onChange={event => setMessage(event.target.value)} disabled={saving} placeholder='Por favor, carga las evidencias pendientes de…' />
              </label>
              <small>{message.length}/2000 caracteres</small>
              <button type='submit' disabled={saving || loading || !message.trim() || !messageMediatorId}>{saving ? 'Enviando…' : 'Enviar mensaje'}</button>
            </form>}
          </div>
        )}
        {notice && <p role='status'>{notice}</p>}

        <div className='reviewer-traffic__summary'>
          <Summary icon='schedule' label='Con plazo disponible' value={summary.open} tone='blue' active={activeFilter === 'open'} onClick={() => toggleFilter('open')} />
          <Summary icon='warning' label='Próximas a vencer' value={summary.dueSoon} tone='yellow' active={activeFilter === 'due_soon'} onClick={() => toggleFilter('due_soon')} />
          <Summary icon='error' label='Vencidas' value={summary.overdue} tone='red' active={activeFilter === 'overdue'} onClick={() => toggleFilter('overdue')} />
          <Summary icon='task_alt' label='Respondidas' value={summary.responded} tone='green' active={activeFilter === 'responded'} onClick={() => toggleFilter('responded')} />
        </div>

        {loading ? <div className='reviewer-traffic__loading'><Loader /></div> : (
          <div className='reviewer-traffic__board-wrap'>
            <div className='reviewer-traffic__board' style={{gridTemplateColumns: `16rem repeat(${weekdays.length}, minmax(11rem, 1fr))`}}>
              <div className='reviewer-traffic__corner' ref={mediatorFilterRef}>
                <span>Mediador</span>
                {isOversightView && <button
                  type='button'
                  className={hasMediatorFilters ? 'is-active' : ''}
                  onClick={() => setShowMediatorFilter(current => !current)}
                  aria-expanded={showMediatorFilter}
                  aria-label='Filtrar mediadores'>
                  <span className='material-symbols-rounded'>filter_list</span>
                  {hasMediatorFilters && <small>{selectedMediatorIds.length || 1}</small>}
                </button>}
                {isOversightView && showMediatorFilter && (
                  <div className='reviewer-traffic__mediator-filter'>
                    <header>
                      <strong>Filtrar mediadores</strong>
                      {hasMediatorFilters && (
                        <button type='button' onClick={() => { setSelectedMediatorIds([]); setMediatorType(''); }}>Limpiar</button>
                      )}
                    </header>
                    <label>
                      <span>Tipo de mediador</span>
                      <select value={mediatorType} onChange={event => setMediatorType(event.target.value)}>
                        <option value=''>Todos los tipos</option>
                        {mediatorTypes.map(type => <option value={type} key={type}>{type}</option>)}
                      </select>
                    </label>
                    <div className='reviewer-traffic__mediator-options'>
                      <span>Seleccionar mediadores</span>
                      {(data?.mediators || []).map(mediator => (
                        <label key={mediator.id}>
                          <input type='checkbox' checked={selectedMediatorIds.includes(Number(mediator.id))} onChange={() => toggleMediator(mediator.id)} />
                          <span><strong>{mediator.fullName}</strong><small>{mediator.roleName}</small></span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {weekdays.map(day => (
                <div className={`reviewer-traffic__day ${toDateKey(day.date) === toDateKey(new Date()) ? 'is-today' : ''}`} key={day.name}>
                  <span>{day.name}</span><strong>{day.date.getDate()}</strong>
                </div>
              ))}
              {visibleMediators.map(mediator => (
                <MediatorRow
                  key={mediator.id}
                  mediator={mediator}
                  weekdays={weekdays}
                  recordsByCell={recordsByCell}
                  onSelect={setSelectedRecord}
                />
              ))}
              {visibleMediators.length === 0 && activeFilter && (
                <div className='reviewer-traffic__no-results'>No hay registros en este estado durante la semana seleccionada.</div>
              )}
            </div>
          </div>
        )}

        <div className='reviewer-traffic__legend'>
          <span className='is-open'>Plazo disponible</span>
          <span className='is-due_soon'>1–2 días hábiles</span>
          <span className='is-overdue'>Vencida</span>
          <span className='is-responded'>Respondida</span>
          <span className='is-closed'>Cerrada</span>
        </div>

        {selectedRecord && (
          <aside className='reviewer-traffic__detail' ref={detailRef}>
            <button type='button' onClick={() => setSelectedRecord(null)} aria-label='Cerrar detalle'>
              <span className='material-symbols-rounded'>close</span>
            </button>
            <span>SOLICITUD DE SUBSANACIÓN</span>
            <h2>{selectedRecord.mediatorName}</h2>
            <dl>
              {selectedRecord.evidenceType !== 'general' && <>
                {selectedRecord.evidenceType === 'incidencia' && (
                  <div><dt>Acción</dt><dd>{selectedRecord.incidenceActionName || 'Acción de incidencia'}</dd></div>
                )}
                <div><dt>Grupo</dt><dd>{selectedRecord.groupCode || '—'}</dd></div>
                {selectedRecord.evidenceType !== 'incidencia' && (
                  <div><dt>Sesión</dt><dd>{selectedRecord.meetingOrder || '—'}</dd></div>
                )}
              </>}
              <div><dt>Solicitada</dt><dd>{formatShortDate(selectedRecord.requestedAt)}</dd></div>
              <div><dt>Fecha límite</dt><dd>{formatShortDate(selectedRecord.dueAt)}</dd></div>
            </dl>
            <strong>Observación</strong>
            <p>{selectedRecord.observations}</p>
            {selectedRecord.evidenceType === 'general' ? (
              <div className='reviewer-traffic__messages'>
                <p>Mensaje general sin evidencia asociada.</p>
                {isReviewerView && !selectedRecord.closedAt && <button type='button' onClick={closeMessage} disabled={saving}>{saving ? 'Cerrando…' : 'Marcar como atendida'}</button>}
                {selectedRecord.closedAt && <p>Solicitud cerrada el {formatShortDate(selectedRecord.closedAt)}.</p>}
              </div>
            ) : <Link
              className='reviewer-traffic__detail-link'
              to={isReviewerView
                ? selectedRecord.evidenceType === 'incidencia'
                  ? '/revisor/evidencias-incidencia'
                  : '/revisor/evidencias'
                : isAdminView
                  ? '/administrador/menu/evidencias'
                : getMediatorEvidencePath(selectedRecord)}
              state={isReviewerView ? {
                evidenceTarget: {
                  mediatorId: selectedRecord.mediatorId,
                  groupId: selectedRecord.groupId,
                  schoolId: selectedRecord.schoolId,
                  evidenceType: selectedRecord.evidenceType,
                  meetingId: selectedRecord.meetingGroupId,
                  incidenceActionId: selectedRecord.incidenceActionId
                }
              } : isAdminView ? undefined : {backTo: '/semaforo'}}>
              <span className='material-symbols-rounded'>fact_check</span>
              {isReviewerView
                ? 'Ir al panel de evidencias'
                : isAdminView
                  ? 'Ir a evidencias'
                : selectedRecord.evidenceType === 'concertacion'
                  ? 'Ir a la concertación'
                  : selectedRecord.evidenceType === 'socializacion'
                    ? 'Ir a la socialización'
                    : selectedRecord.evidenceType === 'incidencia'
                      ? 'Ir a la acción de incidencia'
                    : 'Ir al encuentro'}
              <span className='material-symbols-rounded'>arrow_forward</span>
            </Link>}
          </aside>
        )}

        {showDownloadForm && (
          <div
            className='reviewer-traffic__download-backdrop'
            onMouseDown={event => {
              if (event.target === event.currentTarget && !downloading) setShowDownloadForm(false);
            }}>
            <section className='reviewer-traffic__download-dialog' role='dialog' aria-modal='true' aria-labelledby='traffic-download-title'>
              <header>
                <div>
                  <span>EXPORTAR SEMÁFORO</span>
                  <h2 id='traffic-download-title'>Descargar registros</h2>
                </div>
                <button type='button' onClick={() => setShowDownloadForm(false)} aria-label='Cerrar descarga' disabled={downloading}>
                  <span className='material-symbols-rounded'>close</span>
                </button>
              </header>
              <form onSubmit={handleDownload}>
                <fieldset className='reviewer-traffic__download-period'>
                  <legend>Período</legend>
                  <label>
                    <span>Desde</span>
                    <input type='date' required value={downloadStartDate} onChange={event => setDownloadStartDate(event.target.value)} disabled={downloading} />
                  </label>
                  <label>
                    <span>Hasta</span>
                    <input type='date' required value={downloadEndDate} onChange={event => setDownloadEndDate(event.target.value)} disabled={downloading} />
                  </label>
                </fieldset>
                <fieldset className='reviewer-traffic__download-states'>
                  <legend>Estados</legend>
                  <p>Selecciona uno o varios estados.</p>
                  <div>
                    {TRAFFIC_LIGHT_STATES.map(state => (
                      <label key={state.value}>
                        <input
                          type='checkbox'
                          checked={downloadStates.includes(state.value)}
                          onChange={() => toggleDownloadState(state.value)}
                          disabled={downloading}
                        />
                        <span className={`is-${state.value}`}>{state.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                {downloadError && <p className='reviewer-traffic__download-error' role='alert'>{downloadError}</p>}
                <footer>
                  <button type='button' onClick={() => setShowDownloadForm(false)} disabled={downloading}>Cancelar</button>
                  <button type='submit' disabled={downloading}>
                    <span className='material-symbols-rounded'>download</span>
                    {downloading ? 'Preparando…' : 'Descargar Excel'}
                  </button>
                </footer>
              </form>
            </section>
          </div>
        )}
      </main>
    </section>
  );
}

function Summary({icon, label, value, tone, active, onClick}) {
  return <button type='button' className={`is-${tone} ${active ? 'is-active' : ''}`} onClick={onClick} aria-pressed={active}><span className='material-symbols-rounded'>{icon}</span><div><strong>{value}</strong><small>{label}</small></div></button>;
}

function MediatorRow({mediator, weekdays, recordsByCell, onSelect}) {
  return (
    <>
      <div className='reviewer-traffic__mediator'><span>{mediator.fullName.charAt(0)}</span><div><strong>{mediator.fullName}</strong><small>{mediator.roleName}</small></div></div>
      {weekdays.map(day => {
        const records = recordsByCell.get(`${mediator.id}-${toDateKey(day.date)}`) || [];
        return <div className='reviewer-traffic__cell' key={toDateKey(day.date)}>{records.map(record => (
          <button type='button' className={`is-${record.trackingState}`} key={record.id} onClick={() => onSelect(record)} title={record.observations}>
            <strong>{record.evidenceType === 'general' ? 'Mensaje general' : record.evidenceType === 'concertacion' ? 'Concertación' : record.evidenceType === 'socializacion' ? `Socialización ${record.meetingOrder || 1}` : record.evidenceType === 'incidencia' ? (record.incidenceActionName || 'Acción de incidencia') : (record.groupCode || `Meeting ${record.meetingGroupId}`)}</strong>
            <small>{record.evidenceType === 'general' ? record.observations : record.evidenceType === 'sesion' ? `Sesión ${record.meetingOrder || '—'}` : record.evidenceType === 'incidencia' ? (record.groupCode || 'Acción de incidencia') : (record.schoolName || 'Institución')}</small>
          </button>
        ))}</div>;
      })}
    </>
  );
}

export default ReviewerTrafficLight;
